import { DocumentsListsPolicyModule, InvoicesScheduleModule } from '@folio/modules';
import {
  BadgeCheck,
  CalendarDays,
  Car,
  Euro,
  FilePen,
  FileUp,
  Hash,
  LifeBuoyIcon,
  Mail,
  MapPin,
  PartyPopper,
  Percent,
  Phone,
  Shield,
  User,
  Building2,
} from 'lucide-react';
import type {
  FieldShortcut,
  FormSectionsWithShortcuts,
  KorintModulesProductsConfig,
} from 'korint-sdk/react';
import { driverMtaRoutes } from './common';
import {
  derogationControllerCheckDerogationRequirement,
  derogationControllerCreateDerogation,
  getKorintErrorCode,
  policyControllerPushPolicyEvent,
} from 'korint-sdk';
import { ContactKey } from '@folio/utils/customerDisplayName';
import { IncludedPerilsAssistance } from '../custom-pages/IncludedPerilsAssistance';
import { centsToEuros, eurosToCents } from '@folio/utils';

const customerFunnelSections: FormSectionsWithShortcuts = [
  {
    title: 'Signataire',
    fields: [
      { name: 'contactCivility', icons: { MONSIEUR: User, MADAME: User, MADEMOISELLE: User } },
      {
        name: 'contactFirstName',
        required: true,
        type: 'text',
        isCustomField: true,
        icon: User,
        colSpan: 6 as const,
      },
      {
        name: 'contactLastName',
        required: true,
        type: 'text',
        isCustomField: true,
        icon: User,
        colSpan: 6 as const,
      },
      {
        name: 'contactEmail',
        required: true,
        type: 'email',
        isCustomField: true,
        icon: Mail,
        colSpan: 6 as const,
      },
      {
        name: 'contactPhoneNumber',
        required: true,
        type: 'phoneNumber',
        countries: ['FR'],
        isCustomField: true,
        icon: Phone,
        colSpan: 6 as const,
      },
      {
        name: 'contactBirthDate',
        required: true,
        type: 'date',
        isCustomField: true,
        icon: CalendarDays,
      },
      {
        name: 'contactAddress',
        required: true,
        type: 'address',
        isCustomField: true,
        icon: MapPin,
      },
      {
        name: 'contactPostCode',
        required: true,
        type: 'postcode',
        countries: ['FR'],
        isCustomField: true,
        icon: Hash,
        colSpan: 4 as const,
      },
      {
        name: 'contactCity',
        required: true,
        type: 'city',
        isCustomField: true,
        icon: Building2,
        colSpan: 8 as const,
      },
    ],
  },
];

const assetFields: FieldShortcut[] = [
  {
    name: 'vehicleInput',
    type: 'vehicleInput',
    tabs: ['plate', 'brandModel', 'manual'],
    identityFields: ['registrationNumber', 'brand', 'model'],
  },
];

const quotePolicySections: FormSectionsWithShortcuts = [
  {
    title: 'Frais et commissions',
    fields: [
      {
        name: 'managementFeeTaxesIncluded',
        isCustomField: true,
        type: 'number',
        required: false,
        min: 0,
        icon: Euro,
        colSpan: 6 as const,
        apiDataAccessor: apiData =>
          centsToEuros(apiData.customFields?.managementFeeTaxesIncluded) ?? 0,
        formDataAccessor: formData =>
          eurosToCents((formData.managementFeeTaxesIncluded as number | undefined) ?? 0),
      },
      {
        name: 'retailFee',
        type: 'number',
        required: false,
        min: 0,
        icon: Percent,
        colSpan: 6 as const,
        apiDataAccessor: apiData =>
          apiData.brokerageFees?.find(
            ({ brokerageFirmId }: { brokerageFirmId: string; fee: number }) =>
              brokerageFirmId === apiData.brokerageFirm,
          )?.fee ?? 0,
        formDataAccessor: formData => ({
          fees: [
            {
              brokerageFirmId: formData.brokerageFirm,
              fee: Number(formData.retailFee ?? 0),
            },
          ],
        }),
      },
    ],
  },
];

const quoteAssetSections: FormSectionsWithShortcuts = [
  {
    title: 'Date de début de contrat',
    fields: [
      {
        name: 'startedAt',
        required: true,
        type: 'datetime',
        min: 'now',
        icon: CalendarDays,
      },
    ],
  },
];

const policyHolderInvitationRole = ['DRIVER'];

export const tutassurAssistanceConfig = {
  display: {
    category: 'protection',
    icon: LifeBuoyIcon,
    badges: ['online'],
  },
  productCustomPath: '/assistance',
  policyDashboardEventsModuleConfig: {
    enableAssetComparison: true,
  },
  quoteTierSelectModuleConfig: {
    customIncludedPerils: IncludedPerilsAssistance,
  },
  priceBreakdownConfig: {
    groups: [
      { lines: [{ key: 'INSURANCE_PREMIUM', variant: 'bold' }] },
      { variant: 'callout', lines: [{ key: 'MANAGEMENT_FEE' }] },
      { lines: [{ key: 'RETAIL_COMMISSION', variant: 'bold' }] },
    ],
  },
  customerInformations: {
    fieldsConfig: [
      'contactCivility',
      {
        key: 'contactFirstName',
        type: 'text',
        contactKey: ContactKey.FIRST_NAME,
      },
      {
        key: 'contactLastName',
        type: 'text',
        contactKey: ContactKey.LAST_NAME,
      },
      {
        key: 'contactEmail',
        type: 'email',
      },
      {
        key: 'contactPhoneNumber',
        type: 'tel',
      },
      {
        key: 'contactBirthDate',
        type: 'date',
      },
      {
        key: 'contactAddress',
        type: 'text',
      },
      {
        key: 'contactPostCode',
        type: 'postcode',
        countries: ['FR'],
      },
      {
        key: 'contactCity',
        type: 'text',
      },
    ],
  },
  assetInformations: {
    fieldsConfig: [
      {
        key: 'registrationNumber',
        type: 'text',
      },
      {
        key: 'brand',
        type: 'text',
      },
      {
        key: 'model',
        type: 'text',
      },
    ],
  },
  quoteTierModuleConfig: {
    goToTiersOnMta: '/mta/assistance/${policy.customer}/${policy.id}/new',
    goToTiersOnNewBusiness:
      '/discovery/assistance/${policy.customer}/${policy.id}/${workingBranch?.id}/quote',
    fieldsConfig: [],
    perilsMapping: {},
    isExternalQuoteEnabled: false,
    isChosenTierDisplayed: true,
  },
  policyholderInvitationModuleConfig: {
    relationTypes: policyHolderInvitationRole,
  },
  enableAssetComparison: true,
  discoveryFunnelSteps: {
    afterEndPath: undefined,
    steps: [
      {
        path: 'documents-upload',
        type: 'documentUploadFunnel' as const,
        context: 'policy' as const,
        icon: FileUp,
      },
      {
        path: 'customer',
        type: 'customerFunnel',
        sections: customerFunnelSections,
        icon: User,
      },
      {
        path: 'compliance',
        type: 'complianceFunnel',
        icon: BadgeCheck,
      },
      {
        path: 'vehicle',
        type: 'assetFunnel',
        fields: assetFields,
        minAssets: 1,
        maxAssets: 1,
        icon: Car,
      },
      {
        path: 'quote',
        type: 'quoteFunnel',
        policySections: quotePolicySections,
        assetSections: quoteAssetSections,
        icon: Shield,
      },
      {
        path: 'documents',
        type: 'module',
        icon: FileUp,
        module: (
          <DocumentsListsPolicyModule
            displayContext="policyholder"
            categories={[
              'QUOTE',
              'TERMS_AND_CONDITIONS',
              'IPID_ASSISTANCE',
              'INFORMATION_REPORT',
              'INFORMATION_SHEET',
            ]}
            pollUntilAllDocumentsAreLoaded={true}
          />
        ),
      },
      {
        path: 'invoicing-schedule',
        type: 'module',
        icon: CalendarDays,
        module: <InvoicesScheduleModule />,
      },
      {
        path: 'end',
        type: 'funnelEnd',
        id: 'new-business-end',
        icon: PartyPopper,
        callback: {
          callToActionCallback: async ({ policyId, branchId }) => {
            await policyControllerPushPolicyEvent(
              policyId,
              { eventType: 'TUTASSUR_NB_QUOTE_EMITTED' },
              { branchId },
            );
          },
        },
        additionalComponents: {
          isQuoteTierModuleVisible: true,
        },
      },
    ],
  },
  mtaFunnelSteps: {
    steps: [
      {
        path: 'new',
        type: 'newMTACreation',
        icon: FilePen,
      },
      {
        path: 'customer',
        type: 'customerFunnel',
        sections: customerFunnelSections,
        icon: User,
      },
      {
        path: 'vehicle',
        type: 'assetFunnel',
        fields: assetFields,
        minAssets: 1,
        maxAssets: 1,
        icon: Car,
      },
      {
        path: 'quote',
        type: 'quoteFunnel',
        policySections: quotePolicySections,
        assetSections: quoteAssetSections,
        icon: Shield,
      },
      {
        path: 'invoicing-schedule',
        type: 'module',
        icon: CalendarDays,
        module: <InvoicesScheduleModule />,
      },
      {
        path: 'end',
        type: 'funnelEnd',
        id: 'mta-end',
        icon: PartyPopper,
        additionalComponents: {
          isQuoteTierModuleVisible: true,
        },
        callback: {
          callToActionCallback: async ({ branchId }) => {
            const { needsDerogation } = await derogationControllerCheckDerogationRequirement(
              branchId,
              {},
              { action: 'CONFIRM' },
            );

            if (!needsDerogation) {
              return;
            }

            try {
              await derogationControllerCreateDerogation(branchId, { action: 'CONFIRM' });
            } catch (error) {
              if (getKorintErrorCode(error) !== 'DEROGATION_ALREADY_EXISTS') {
                throw error;
              }
            }
          },
        },
      },
    ],
  },
  policyholderMtaFunnelSteps: driverMtaRoutes,
  finalizeNewBusinessModuleConfig: {
    buttons: [
      {
        id: 'send-via-email-after-derogation-approval',
        actionType: 'signature',
        visual: {
          label: 'Envoi du contrat au client',
          icon: 'mail',
          successMessage: 'Contrat envoyé par email',
        },
        signature: {
          signatureType: 'direct',
          deliveryMode: 'EMAIL',
          otp: {
            enabled: false,
          },
        },
        visibilityConditions: {
          policyStatuses: ['POLICY_QUOTED'],
          branchTypes: ['MTA'],
          derogationStatuses: ['APPROVED'],
        },
      },
      {
        id: 'send-via-email',
        actionType: 'signature',
        visual: {
          label: 'Envoi du contrat au client',
          icon: 'mail',
          successMessage: 'Contrat envoyé par email',
        },
        signature: {
          signatureType: 'direct',
          deliveryMode: 'EMAIL',
          otp: {
            enabled: false,
          },
        },
        visibilityConditions: {
          firmTypes: ['RETAIL', 'WHOLESALE'],
          policyStatuses: ['POLICY_QUOTED'],
          branchTypes: ['NEW_BUSINESS'],
          needsDerogation: false,
        },
      },
    ],
    rules: [
      {
        id: 'documents-import',
        link: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}/documents`,
        isValid: ({ policy }) => policy.customFields.allClientDocumentsOk === true,
      },
      {
        id: 'sepa-payment',
        link: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}/payment-methods`,
        isValid: ({ paymentMethods }) =>
          paymentMethods.some(({ paymentMethodType }) => paymentMethodType === 'sepa_debit'),
      },
    ],
  },
  brokerPolicyDashboardHome: {
    isFinalizeNewBusinessModuleVisible: true,
    isAuthorCardModuleVisible: true,
    isPolicyholderInvitationModuleVisible: true,
    isQuoteTierModuleVisible: true,
    isPremiumModuleVisible: true,
    isPolicyLifecycleSummaryModuleVisible: true,
    isInvoicesBalanceModuleVisible: true,
  },
} satisfies KorintModulesProductsConfig;
